import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { withRoomClientContext } from '../../lib/RoomClientContext';
import CheckPermissionsDialog from './CheckPermissionsDialog';

import {
  setAudioinputDevices,
  setAudiooutputDevices,
  setVideoinputDevices,
} from '../../store/slices/deviceSlice';

function CheckPermissions({ roomClient, children }) {
  const [loading, setLoading] = useState(true);
  const [checkedPermission, setCheckedPermission] = useState(false);
  const [tryIdx, setTryIdx] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const devices = await roomClient.streamHandler.getDevices();

      setCheckedPermission(
        devices.every((d) => !!d.label || d.deviceId === 'default')
      );
      setLoading(false);
    })();
  }, [roomClient.streamHandler]);

  useEffect(() => {
    if (!checkedPermission) return;

    (async () => {
      const devices = await roomClient.streamHandler.getDevices();
      // Firefox is super buggy with this behaviour,
      // so we do some retries to make sure we get the devices
      const noLabels = devices.every((d) => !d.label);
      if (noLabels && tryIdx < 5) {
        setTimeout(() => {
          setTryIdx(tryIdx + 1);
        }, 100);
        return;
      }

      const videoinputDevices = devices.filter(
        (d) => d.kind === 'videoinput' && d.deviceId !== ''
      );
      const audioinputDevices = devices.filter(
        (d) => d.kind === 'audioinput' && d.deviceId !== ''
      );
      const audiooutputDevices = devices.filter(
        (d) => d.kind === 'audiooutput' && d.deviceId !== ''
      );

      if (audiooutputDevices.length === 0) {
        // Set the default audio output device
        audiooutputDevices.push({
          deviceId: 'notsupported',
          groupId: 'default',
          label: t('Default'),
        });
      }

      dispatch(setVideoinputDevices(videoinputDevices));
      dispatch(setAudioinputDevices(audioinputDevices));
      dispatch(setAudiooutputDevices(audiooutputDevices));

      setLoading(false);
    })();
  }, [checkedPermission, tryIdx, roomClient.streamHandler, dispatch, t]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!checkedPermission) {
    return (
      <CheckPermissionsDialog
        open={!checkedPermission}
        onClose={() => setCheckedPermission(true)}
        streamHandler={roomClient.streamHandler}
      />
    );
  }

  return children;
}

const CheckPermissionWrapper = ({ roomClient, children }) => {
  return <CheckPermissions roomClient={roomClient} children={children} />;
};

export default withRoomClientContext(CheckPermissionWrapper);
